/**
 * TelemetryPageViews — fires `page.viewed` / `page.left` / `session.started`
 * lifecycle events from the router (Track C event activation).
 *
 * - `page.viewed`   on every route change (and the first load), with the
 *   logical template + path (+ loadMs on the first paint).
 * - `page.left`     on route change away (exitType "navigation") and on
 *   visibilitychange → hidden (exitType "close").
 *
 * Must be rendered inside a Router (uses `useLocation`).
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { pageLeft, pageView } from '../lib/telemetry';

function templateForPath(pathname: string): string {
  if (pathname === '/' || pathname.startsWith('/home')) return 'home';
  if (pathname.startsWith('/services/search-list')) return 'search-results';
  if (pathname.startsWith('/services/service-details')) return 'service-detail';
  if (pathname.startsWith('/service/booking')) return 'booking-wizard';
  if (pathname.startsWith('/booking/success')) return 'booking-confirmation';
  if (pathname.startsWith('/business')) return 'business-detail';
  if (pathname.startsWith('/customer')) return 'customer-dashboard';
  if (pathname.startsWith('/admin')) return 'admin';
  return 'other';
}

function getScrollPct(): number {
  try {
    const doc = document.documentElement;
    const max = doc.scrollHeight - window.innerHeight;
    if (max <= 0) return 0;
    return Math.min(100, Math.round((window.scrollY / max) * 100));
  } catch {
    return 0;
  }
}

function getLoadMs(): number | undefined {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (nav) return Math.round(nav.loadEventEnd - nav.startTime);
  } catch {
    /* ignore */
  }
  return undefined;
}

export function TelemetryPageViews() {
  const location = useLocation();
  const firstRef = useRef(true);
  const didLeaveRef = useRef(false);
  const enteredAtRef = useRef(Date.now());
  const lastPathRef = useRef(location.pathname);
  const lastTrackedRef = useRef<{ path: string; time: number } | null>(null);

  // page.left for the outgoing route + page.viewed for the new route.
  useEffect(() => {
    const path = location.pathname;
    const now = Date.now();

    // Suppress React StrictMode's double-invoke (same path within 1s).
    if (
      lastTrackedRef.current &&
      lastTrackedRef.current.path === path &&
      now - lastTrackedRef.current.time < 1000
    ) {
      return;
    }
    lastTrackedRef.current = { path, time: now };

    const template = templateForPath(path);

    if (
      !firstRef.current &&
      lastPathRef.current !== path &&
      !didLeaveRef.current
    ) {
      didLeaveRef.current = true;
      pageLeft({
        template: templateForPath(lastPathRef.current),
        path: lastPathRef.current,
        dwellMs: now - enteredAtRef.current,
        maxScrollPct: getScrollPct(),
        exitType: 'navigation',
      });
    }

    let loadMs: number | undefined;
    if (firstRef.current) {
      loadMs = getLoadMs();
      firstRef.current = false;
    }

    pageView(template, path, loadMs);
    didLeaveRef.current = false;
    enteredAtRef.current = now;
    lastPathRef.current = path;
  }, [location.pathname]);

  // Tab closed / backgrounded → page.left (exitType "close").
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden' && !didLeaveRef.current) {
        didLeaveRef.current = true;
        pageLeft({
          template: templateForPath(lastPathRef.current),
          path: lastPathRef.current,
          dwellMs: Date.now() - enteredAtRef.current,
          maxScrollPct: getScrollPct(),
          exitType: 'close',
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  return null;
}

export default TelemetryPageViews;
